import { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Flex } from 'reflexbox';
import styled from 'styled-components';

import { useAuthenticatedUser, useFeature } from '@treams/treams-hooks';
import {
	ConversationIcon,
	DialogueIcon,
	HeartIcon,
	TargetIcon,
	ViewIcon,
	VisibilityOffIcon,
} from '@treams/treams-icons';
import * as graphql from '@treams/treams-network';
import { Button, Subheading, Table, Title, tokens } from '@treams/treams-ui';

import { CHECKBOX_WIDTH, JOB_WIDTH, NAME_WIDTH } from './InboxManagerTable';

const Actions = styled(Box)<{ $padTop?: boolean }>`
	display: grid;
	grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
	gap: ${tokens.gap.list}px;
	width: 100%;
	padding-top: ${(props) => (props.$padTop ? tokens.gap.section : 0)}px;
`;

const ActionHolder = styled(Box)`
	min-width: 0;

	a {
		display: block;
		width: 100%;
	}
`;

const ActionButton = styled(Button)`
	width: 100%;
	min-width: 0 !important;
	justify-content: center;
`;

function Action(props: { icon: ReactNode; title: string; link: string }) {
	return (
		<ActionHolder>
			<ActionButton type="secondary" icon={props.icon} title={props.title} link={props.link} />
		</ActionHolder>
	);
}

export default function InboxManagerHeader(props: {
	sidebar: boolean;
	header?: boolean;
	small?: boolean;
	toggleSidebar: () => void;
}) {
	const { t } = useTranslation();
	const viewer = useAuthenticatedUser();

	const hasConversations = useFeature(graphql.FeatureName.Evaluations);
	const hasCompliments = useFeature(graphql.FeatureName.Compliments);
	const hasFeedback = useFeature(graphql.FeatureName.Feedback);
	const hasTeamGoals = useFeature(graphql.FeatureName.TeamGoals);
	const isAdhocEnabled = useFeature(graphql.FeatureName.EvaluationsAdhoc);

	return (
		<Box>
			{props.header !== false && (
				<Flex mb={4} alignItems="center">
					<Title flexGrow={1}>{t('inbox.title', { name: viewer.firstName })}</Title>
					<Button link="/inbox/personal" title={t('managerInbox.personalInboxButton.title')} />
				</Flex>
			)}

			<Actions $padTop={props.header !== false}>
				{hasTeamGoals && (
					<Action
						icon={<TargetIcon />}
						title={t('managerInbox.actions.addTeamGoal.title')}
						link="/goals/new?group_id="
					/>
				)}
				{hasCompliments && (
					<Action
						icon={<HeartIcon />}
						title={t('managerInbox.actions.giveCompliment.title')}
						link="/feedback/compliments/new"
					/>
				)}
				{hasFeedback && (
					<Action
						icon={<DialogueIcon />}
						title={t('managerInbox.actions.askFeedback.title')}
						link={`/users/${viewer.id}/feedback/request-from-moment`}
					/>
				)}
				{hasConversations && isAdhocEnabled && (
					<Action
						icon={<ConversationIcon />}
						title={t('managerInbox.actions.startConversation.title')}
						link={`/conversations/request`}
					/>
				)}
			</Actions>

			{!props.small && (
				<Flex mt={`${tokens.gap.page}px`} mb={`${tokens.gap.group}px`} alignItems="center">
					<Subheading flexGrow={1}>{t('managerInbox.team.title')}</Subheading>
					<Button
						type="secondary"
						icon={props.sidebar ? <VisibilityOffIcon /> : <ViewIcon />}
						onClick={() => props.toggleSidebar()}
						title={
							props.sidebar
								? t('managerInbox.hideFiltersButton.title')
								: t('managerInbox.showFiltersButton.title')
						}
					/>
				</Flex>
			)}
		</Box>
	);
}

export function InboxManagerTableHeader() {
	const { t } = useTranslation();

	return (
		<Table.Header>
			<Table.Cell width={CHECKBOX_WIDTH} />
			<Table.Cell width={NAME_WIDTH}>{t('managerInbox.table.header.name')}</Table.Cell>
			<Table.Cell width={JOB_WIDTH}>{t('managerInbox.table.header.jobPosition')}</Table.Cell>
			<Table.Cell grow>{t('managerInbox.table.header.status')}</Table.Cell>
		</Table.Header>
	);
}
